import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  useMediaQuery,
  Alert as MuiAlert,
  Snackbar,
  AlertTitle
} from '@mui/material';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { LoadingContext, LoadingBar, DefaultURl } from '../../M_Components';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import PropTypes from 'prop-types';

// Realm-scoped storage keys (only to pick the right token)
import { STORAGE_CARRIER, STORAGE_USER } from 'src/components/auth/storageKeys';

// Realm-scoped Redux store factory (prevents user/carrier tabs from leaking)
import { createStoreForRealm } from '../M_Store/index';

/*
* Root "shell" component that wraps:
* * Redux Provider + PersistGate
* * Loading + Toast context (LoadingContext)
* * Global Axios interceptors for:
* * Injecting Authorization header based on active realm (user/carrier)
* * Rewriting specific endpoints (exs:"/x?y" -> "/x/query?y")
* * Handling 401 -> redirect to realm-specific LOGIN page
*
* It does NOT know about React Router directly; instead it receives the already
* built "Routing" tree as a prop and renders it inside the providers.
  */
export function MRouter({
  Routing,
  url,
  primaryColor,
  secondaryColor,
  tertiaryColor,
  endpoints
}) {
  // ---------------------------------------------------------------------------
  // Global loading overlay flag (simple boolean)
  // ---------------------------------------------------------------------------
  const [isLoad, setIsLoad] = useState(false);

  // Snackbar / toast state
  const [isOpenToast, setIsOpenToast] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [colorSnack, setColorSnack] = useState('');
  const [alertFunction, setAlertFunction] = useState('');

  // Misc formatting / shared data state
  const [useComma, setUseComma] = useState(true);
  const [passValue, setPassValue] = useState(new Map());

  const { t } = useTranslation();

  // Base API URL (can be injected or default to config)
  const DEFAULTURL = url || DefaultURl;

  // Used only for the loading overlay spacing
  const matches = useMediaQuery('(min-width:1200px)');

  // Optional list of endpoints to rewrite "/x?y" -> "/x/query?y"
  endpoints = endpoints || [];

  // ---------------------------------------------------------------------------
  // Realm helpers (user vs carrier)
  // ---------------------------------------------------------------------------

  /**
  * Detect active realm based on current hash path.
  * HashRouter produces URLs like "#/carrier/..." and "#/home/...".
  */
  const getActiveRealm = React.useCallback(() => {
    const h = window.location.hash || '';
    return h.startsWith('#/carrier') ? 'carrier' : 'user';
  }, []);

  /**
  * IMPORTANT:
  * We create a realm-scoped store/persistor so that persisted UI state (tabs, etc.)
  * does NOT leak between user and carrier realms (same origin / shared localStorage).
  */
  // Freeze realm per tab load to avoid recreating store/persistor during navigation
  const [realm] = useState(() => getActiveRealm());
  const { store, persistor } = useMemo(() => createStoreForRealm(realm), [realm]);

  /**
  * Returns the correct auth token based on the active realm.
  */
  const getRealmToken = React.useCallback(() => {
    const active = getActiveRealm();
    if (active === 'carrier') {
      return localStorage.getItem(STORAGE_CARRIER?.token) || null;
    }

    // NOTE:
    // We keep the legacy "token" fallback only if you still rely on it elsewhere.
    // Once the user login flow fully writes to STORAGE_USER.token and all readers
    // are updated, remove the legacy fallback to avoid cross-realm confusion:
    //
    //   return localStorage.getItem(STORAGE_USER?.token) || null;
    //
    return localStorage.getItem(STORAGE_USER?.token) || null;

  }, [getActiveRealm]);

  const buildHashUrl = React.useCallback((hashPath) => {
    return `${window.location.origin}/#${hashPath.startsWith('/') ? hashPath : `/${hashPath}`
      }`;
  }, []);

  const redirectToRealmLogin = React.useCallback(
    (activeRealm) => {
      const target =
        activeRealm === 'carrier'
          ? buildHashUrl('/carrier/login')
          : buildHashUrl('/login');


      const current = window.location.href;
      if (current !== target) {
        window.location.href = target;
      }
    },
    [buildHashUrl]


  );

  const isAuthFreeRequest = React.useCallback((urlLower) => {
    return (
      // USER LOGIN / FORGOT PASSWORD
      urlLower.includes('identity/login') ||
      urlLower.includes('identity/forgetpassword') ||


      // CARRIER LOGIN / FORGOT PASSWORD
      urlLower.includes('carrieridentity/login') ||
      urlLower.includes('carrieridentity/password/forgot') ||
      urlLower.includes('carrieridentity/forgetpassword') ||

      // CARRIER INVITE FLOWS (PUBLIC)
      urlLower.includes('/carrier-invite/') ||
      urlLower.includes('carrierinvitespot') ||
      urlLower.includes('carrierinvitephase')
    );


  }, []);

  // ---------------------------------------------------------------------------
  // Axios interceptors: request + response
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const activeRealm = getActiveRealm();
      const token = getRealmToken();


      const reqUrl = (config.url || '').toLowerCase();
      const authFree = isAuthFreeRequest(reqUrl);

      // Attach Authorization header for protected requests
      if (token && !authFree) {
        config.headers = config.headers || {};
        // NOTE: no trailing whitespace after token
        config.headers['Authorization'] = `Bearer ${token} `;
      }

      // Optional rewrite: '/{endpoint}?query' -> '/{endpoint}/query?query'
      if (endpoints && endpoints.length > 0) {
        let patched = false;
        for (const end of endpoints) {
          if (patched) break;

          // IMPORTANT: no accidental space in the template literal
          if (config.url?.includes(`/ ${end}?`)) {
            config.url = config.url.replace('?', '/query?');
            patched = true;
          }
        }
      }

      // NOTE:
      // We intentionally do NOT redirect here when there is no token.
      // Pre-flight redirects from the request interceptor can easily
      // break the "setIsLoad(true) / setIsLoad(false)" symmetry in callers,
      // leaving the global loading overlay stuck. Instead, we rely on
      // the backend to return 401 and handle redirects in the response
      // interceptor below.

      // If you still want a very early guard for completely missing tokens,
      // you could optionally re-enable:
      //
      //   if (!authFree && !token) {
      //     const active = getActiveRealm();
      //     redirectToRealmLogin(active); // or realm entry, depending on flow
      //   }
      //
      // but be aware of the loading implications.

      return config;
    });

    const resId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const urlLower = error?.config?.url?.toLowerCase() || '';
        const authFree = isAuthFreeRequest(urlLower);

        // Handle unauthorized responses only for protected endpoints
        if (error?.response?.status === 401 && !authFree) {
          const active = getActiveRealm();
          redirectToRealmLogin(active);
        }

        // Always reject so BufferService and other callers can normalize
        // errors in a single place.
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };


  }, [endpoints, getActiveRealm, getRealmToken, isAuthFreeRequest, redirectToRealmLogin]);

  // ---------------------------------------------------------------------------
  // Loader timeout safeguard (boolean isLoad)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoad === true) {
        setIsLoad(false);
        toastMessages('error', 'Load Timeout', 'ERROR ');
      }
    }, 300000); // 5 minutes


    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps


  }, [isLoad]);

  // ---------------------------------------------------------------------------
  // Toast / snackbar helpers
  // ---------------------------------------------------------------------------
  const toastMessages = (type, alert, string) => {
    setIsOpenToast(true);
    setColorSnack(type);
    setAlertMessage(alert);
    setAlertFunction(string);
  };

  const handleClose = (reason) => {
    if (reason === 'clickaway') return;
    setIsOpenToast(false);
  };

  function alertTitleMessage() {
    if (colorSnack === 'error') return t('Error');
    if (colorSnack === 'success') return t('Success');
    if (colorSnack === 'info') return t('InformationLabel');
    return '';
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (<Provider store={store}> <PersistGate loading={null} persistor={persistor}>
    {/* Loading + Toast context shared across the app */}
    <LoadingContext.Provider
      value={useMemo(
        () => ({
          isLoad,
          setIsLoad,
          isOpenToast,
          setIsOpenToast,
          alertMessage,
          setAlertMessage,
          colorSnack,
          setColorSnack,
          alertFunction,
          setAlertFunction,
          toastMessages,
          handleClose,
          useComma,
          setUseComma,
          DEFAULTURL,
          primaryColor,
          secondaryColor,
          tertiaryColor,
          passValue,
          setPassValue
        }),
        [
          isLoad,
          isOpenToast,
          alertMessage,
          colorSnack,
          alertFunction,
          useComma,
          passValue,
          DEFAULTURL,
          primaryColor,
          secondaryColor,
          tertiaryColor
        ]
      )}
    >
      {/* Global loading overlay (blocks UI while isLoad === true) */}
      <div
        style={
          isLoad
            ? {
              position: 'fixed',
              height: '100vh',
              width: '100%',
              zIndex: '5000000',
              top: '0',
              backgroundColor: 'var(--color-grey-light)',
              opacity: '0.5',
              overflow: 'hidden'
            }
            : {
              position: 'absolute',
              height: '100%',
              width: '100%',
              display: 'none'
            }
        }
      >
        <div style={matches ? { height: '50px' } : { height: '100px' }} /> <LoadingBar /> </div>


      {/* Main routing tree (React Router) provided from outside */}
      {Routing}

      {/* Global snackbar/toast container */}
      <Snackbar
        disableWindowBlurListener={true}
        open={isOpenToast}
        autoHideDuration={5000}
        onClose={handleClose}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleClose}
          severity={colorSnack || null}
          sx={{ width: '100%' }}
        >
          <AlertTitle style={{ textTransform: 'capitalize' }}>
            {alertTitleMessage()}
          </AlertTitle>
          {alertFunction}
          <b>{alertMessage}</b>
        </MuiAlert>
      </Snackbar>
    </LoadingContext.Provider>
  </PersistGate>
  </Provider>


  );
}

MRouter.propTypes = {
  Routing: PropTypes.element,
  url: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  tertiaryColor: PropTypes.string,
  endpoints: PropTypes.array
};
